import { cn } from "@/lib/utils";

/** "xs" — number only, no caption, for compact rail rows. "sm" — number + caption. "lg" — hero, adds tier label. */
export type FitScoreSize = "xs" | "sm" | "lg";

export interface FitScoreRingProps {
  /** Computed fit score, 0–100, or null when the org has not yet been scored. */
  score: number | null;
  /**
   * Named step ("xs" | "sm" | "lg") or an explicit pixel size. A number renders a
   * compact, number-only ring (no caption, no tier label) at that box size — used
   * by the Explore card (48), map popover (42), and list row (40). All four states
   * (strong / moderate / low / not-yet-scored) are identical to the named sizes.
   */
  size?: FitScoreSize | number;
  className?: string;
  /**
   * The score is being worked out (#1129): a pulsing dashed track and
   * "Calculating", never the not-yet-scored em-dash. `score` is ignored.
   */
  calculating?: boolean;
}

type Tier = "strong" | "moderate" | "low" | "none";

/**
 * Canonical fit-score component — the single ring used everywhere a fit/match
 * score is shown. Four states (strong / moderate / low / not-yet-scored) and two
 * sizes, matching the signature-component page of the design guidelines.
 *
 * Tier colors are pulled from the design tokens (src/index.css), never hardcoded:
 *   strong   80–100  teal primary   — reads positive
 *   moderate 50–79   neutral ink    — warm, neutral
 *   low      0–49    muted cool     — never an alarming red
 *   none     null    dashed track + em-dash — never a hard 0% or a filled ring
 */
const getTier = (score: number | null): Tier => {
  if (score === null || score === undefined || Number.isNaN(score)) return "none";
  if (score >= 80) return "strong";
  if (score >= 50) return "moderate";
  return "low";
};

const TIER_STROKE: Record<Tier, string> = {
  strong: "hsl(var(--primary))",
  moderate: "hsl(var(--ink-500))",
  low: "hsl(var(--chart-seq-4))",
  none: "hsl(var(--border))",
};

const TIER_LABEL: Record<Tier, string> = {
  strong: "Strong match",
  moderate: "Moderate match",
  low: "Low match",
  none: "Not yet scored",
};

/**
 * The canonical tier word ("Strong match" / "Moderate match" / "Low match" /
 * "Not yet scored") for a score, using the same thresholds the ring itself draws
 * with. Exposed so surfaces that render a numeric ring (which has no built-in
 * caption) can show the matching label beside it without re-deriving the tiers —
 * e.g. the Compare header cell.
 */
export const fitTierLabel = (score: number | null): string => TIER_LABEL[getTier(score)];

const SIZES = {
  /** Compact ring for feed-rail rows — number only, no caption text. */
  xs: {
    box: 64,
    stroke: 5,
    number: "text-lg",
    dash: "text-2xl",
    caption: "" as string,
    showCaption: false,
    showTier: false,
  },
  /** Standard inline ring for list rows, modals, and map cards. */
  sm: {
    box: 64,
    stroke: 5,
    number: "text-lg",
    dash: "text-2xl",
    caption: "text-[8px]",
    showCaption: true,
    showTier: false,
  },
  /** Hero ring for the organization profile — adds the tier label below. */
  lg: {
    box: 112,
    stroke: 6,
    number: "text-4xl",
    dash: "text-4xl",
    caption: "text-[10px]",
    tier: "text-sm",
    showCaption: true,
    showTier: true,
  },
} as const;

const FitScoreRing = ({ score, size = "sm", className, calculating = false }: FitScoreRingProps) => {
  const tier = calculating ? "none" : getTier(score);
  // A numeric size renders a compact, caption-less ring at that exact box size;
  // string sizes keep the canonical named presets.
  const numeric = typeof size === "number";
  const cfg = numeric
    ? {
        box: size as number,
        stroke: Math.max(4, Math.round((size as number) * 0.1)),
        number: "",
        dash: "",
        caption: "",
        showCaption: false,
        showTier: false,
      }
    : SIZES[size];
  // Inline font sizing for numeric rings (the named presets use Tailwind classes).
  const numberStyle = numeric ? { fontSize: Math.round(cfg.box * 0.34) } : undefined;
  const dashStyle = numeric ? { fontSize: Math.round(cfg.box * 0.42) } : undefined;
  const radius = (cfg.box - cfg.stroke) / 2;
  const center = cfg.box / 2;
  const circumference = 2 * Math.PI * radius;

  const clamped = tier === "none" ? 0 : Math.max(0, Math.min(100, score as number));
  // Progress arc fills proportionally to the score.
  const dashOffset = circumference * (1 - clamped / 100);

  const ariaLabel = calculating
    ? "Fit score: calculating"
    : tier === "none"
      ? "Fit score: not yet scored"
      : `Fit score: ${Math.round(clamped)} out of 100, ${TIER_LABEL[tier]}`;

  return (
    <div className={cn("inline-flex flex-col items-center", className)}>
      <div
        className="relative"
        style={{ width: cfg.box, height: cfg.box }}
        role="img"
        aria-label={ariaLabel}
        data-fit-state={calculating ? "calculating" : tier}
      >
        <svg
          width={cfg.box}
          height={cfg.box}
          className={cn("-rotate-90", calculating && "animate-pulse")}
          aria-hidden="true"
        >
          {tier === "none" ? (
            // Not-yet-scored: dashed track only — no progress fill, never a hard 0%.
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              strokeWidth={cfg.stroke}
              strokeLinecap="round"
              strokeDasharray="2 6"
              style={{ stroke: "hsl(var(--fit-none))" }}
            />
          ) : (
            <>
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                strokeWidth={cfg.stroke}
                style={{ stroke: "hsl(var(--fit-track, var(--border)))" }}
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                strokeWidth={cfg.stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                style={{
                  stroke: TIER_STROKE[tier],
                  transition: "stroke-dashoffset 0.8s ease-out",
                }}
              />
            </>
          )}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {calculating ? (
            <span
              className={cn("font-semibold leading-none text-muted-foreground animate-pulse", cfg.dash)}
              style={dashStyle}
              aria-hidden="true"
            >
              …
            </span>
          ) : tier === "none" ? (
            <span
              className={cn("font-semibold leading-none text-muted-foreground", cfg.dash)}
              style={dashStyle}
              aria-hidden="true"
            >
              —
            </span>
          ) : (
            <span
              className={cn(
                "font-bold leading-none tabular-nums text-foreground",
                cfg.number,
              )}
              style={numberStyle}
            >
              {Math.round(clamped)}
            </span>
          )}
          {cfg.showCaption && (
            <span
              className={cn(
                "mt-1 font-semibold uppercase tracking-[0.12em] text-muted-foreground",
                cfg.caption,
              )}
            >
              {calculating ? "Calculating" : tier === "none" ? "Not yet scored" : "Fit Score"}
            </span>
          )}
        </div>
      </div>

      {cfg.showTier && (
        <span className={cn("mt-2 font-semibold text-foreground", "tier" in cfg ? cfg.tier : "")}>
          {calculating ? "Calculating…" : TIER_LABEL[tier]}
        </span>
      )}
    </div>
  );
};

export default FitScoreRing;
